import React, { Suspense, useEffect, useRef, useState } from 'react';

import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import { useDebouncedCallback } from 'use-debounce';

import { BackLink } from '../../components/atoms';
import { Hidden, LoadingSpinner, Page } from '../../components/layouts';
import {
  CampaignConsentDialog,
  TableNumberHeader,
} from '../../components/partials';
import { AddCreditCardDialog } from '../../components/partials/AddCreditCardDialog/AddCreditCardDialog';
import { NewVoucherPopout } from '../../components/partials/NewVoucherPopout';
import { PageHeader } from '../../components/partials/PageHeader';
import { analyticsService, flagService } from '../../core/services';
import { domUtils, urlUtils, utils } from '../../utils';
import { usePrevious } from '../../utils/hooks';
import * as Api from '../../weborder/Api';
import * as BasketControl from '../../weborder/BasketControl';
import {
  DispatchProps,
  LoadStatus,
  StateProps,
} from '../../weborder/component/Checkout';
import { RouteParams } from '../../weborder/Routes';
import { OrderPayload } from '../../weborder/selectors/basketSelectors';
import { MENU_HAS_UPDATED_ERROR_CODE } from './constants';
import {
  BasketSection,
  EmailDialog,
  OrderProcessing,
  PaymentSection,
  RestaurantVoucherSection,
  SelectTimeDialog,
  ServiceSection,
  TableSelectDialog,
  VoucherDialog,
} from './partials';
import { checkoutUtils } from './utils';
import { clearBasketAndMenuLocalStorage } from './utils/checkoutUtils';

const ProfilePanel = React.lazy(
  () => import('../../components/partials/ProfilePanel'),
);

type Props = StateProps & DispatchProps & RouteParams;

export function OrderCheckout(props: Props): JSX.Element {
  const {
    account,
    actions,
    AddItemToBasket: addItemToBasket,
    AppConfig: appConfig,
    basket,
    cancelAddCardBridge,
    cancelCheckoutFlow,
    checkoutError,
    checkoutValidationErrors,
    checkUserVoucher,
    checkVoucher,
    ChefNotes: chefNotes,
    clearBasket,
    clearBasketStatus,
    continueCheckoutFlow,
    createBasketSnapshot,
    creditCardPaymentAdded,
    Customer: customer,
    DeliveryLocation: deliveryLocation,
    EditChefNotes: editChefNotes,
    editPickupLocationTypes,
    EditTableNumbers: editTableNumbers,
    getMenuByUrl,
    getRestaurantData,
    hasRequestFullNameFeatureFlag,
    initializeStripe,
    isBasketEmpty,
    isBasketLoaded,
    isOrderProcessing,
    isRoundUpEnabled,
    Languages: languages,
    loading,
    loginErrorBridge,
    loginSuccessBridge,
    loyaltyCampaign,
    menu,
    menuUrl,
    Order: order,
    orderItemTotal,
    orderPayload,
    orderRoundUpAmount,
    orderTipsTotal,
    orderTotal,
    orderTotalToPay,
    params,
    payments,
    PercentageTip: percentageTip,
    PickupLocationType: pickupLocationType,
    RemoveItemFromBasket: removeItemFromBasket,
    requestedTimeOrDefault,
    restaurant,
    router,
    SetCustomer: setCustomer,
    setExcludeServiceCharge,
    SetOrder: setOrder,
    SetRequestedTime: setRequestedTime,
    setRoundUpEnabled,
    setShowAddCardDialog,
    SetTip: setTip,
    setUserName,
    showAddCardDialog,
    startCheckoutFlow,
    TableNumber: tableNumber,
    ToastMessages: toastMessages,
    translate,
    UpdateCustomer: updateCustomer,
    UserName: userName,
    VouchersInProgress: vouchersInProgress,
    vouchers,
  } = props;

  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme?.breakpoints.down('xs'));

  const { type } = params;
  const concessionStoreId = Number(params.concessionStoreId);
  const id = Number(params.id);
  const vrId = Number(params.vrid);

  // TODO move this to 1 top component that handles global stuff like this
  const loggedInCheck = (): void => {
    const originalAlert = window.alert;
    window.alert = () => true;

    axios
      .get('/account/accountdetails')
      .then(() => {
        window.alert = originalAlert;

        loginSuccessBridge();
      })
      .catch(() => {
        window.alert = originalAlert;

        loginErrorBridge();
        setCustomer({ status: 'none' });

        urlUtils.navigateToHash('/phone');
        axios.post('/Account/LogOff').catch((_error) => {
          // console.log(error);
        });
      });
  };

  const setErrorFromUrl = (locationParams): void => {
    const error = checkoutUtils.getErrorFromUrl(locationParams);
    if (error) {
      setPaymentError(error);
    }
  };

  const [currentLang, setCurrentLang] = useState(
    utils.get.activeLanguageCode(languages, 'en'),
  );
  const [paymentError, setPaymentError] = useState(null);
  const [pendingVouchers, setPendingVouchers] = useState([]);
  const [pushedMessages, setPushedMessages] = useState([]);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [showTableServiceDialog, setShowTableServiceDialog] = useState(false);
  const [showVoucherDialog, setShowVoucherDialog] = useState(false);

  const isLoyaltyEnabled = flagService.isFlagOn('loyaltyStatus');
  const restaurantDetailLoadStatus = useRef<LoadStatus>(
    restaurant ? 'LOADED' : 'NOT_LOADED',
  );
  const menuLoadStatus = useRef<LoadStatus>('NOT_LOADED');

  const prevMenuUrl = usePrevious(menuUrl);

  const isBasketMenuLoaded =
    menu?.MenuId !== 0 && menu?.MenuId === basket?.menuId;

  const toggleEmailDialog = (): void => setShowEmailDialog(!showEmailDialog);

  const toggleTableDialog = (): void =>
    setShowTableServiceDialog(!showTableServiceDialog);

  const toggleVoucherDialog = (): void =>
    setShowVoucherDialog(!showVoucherDialog);

  const toggleExcludeServiceCharge = (): void =>
    setExcludeServiceCharge(!basket.excludeServiceCharge);

  const { enqueueSnackbar } = useSnackbar();

  const [checkVoucherState, cancelCheckVoucherState] = useDebouncedCallback(
    (newOrderPayload: OrderPayload) => {
      checkVoucher({ orderPayload: newOrderPayload });
    },
    1200,
    {
      // immediately run on first call, next calls debounce
      leading: true,
    },
  );

  /* TODO - create custom hooks to handle high volume of useEffects
   possible hooks - useMenu, useRestaurantDetails, useCheckoutErrors, etc
   can possibly be shared with RestaurantMenu (not useCheckoutErrors) */
  useEffect(() => {
    domUtils.skipToContent();
  }, []);

  useEffect(() => {
    setErrorFromUrl(router);
  }, []);

  useEffect(() => {
    loggedInCheck();
  }, []);

  useEffect(() => {
    if (payments.length > 0) {
      initializeStripe();
    }
  }, [payments.length]);

  useEffect(() => {
    if (!restaurant && restaurantDetailLoadStatus.current === 'NOT_LOADED') {
      getRestaurantData(vrId, type, id);
      restaurantDetailLoadStatus.current = 'LOADING';
    }
  }, [restaurant]);

  useEffect(() => {
    if (restaurant) {
      restaurantDetailLoadStatus.current = 'LOADED';
    }
  }, [restaurant]);

  useEffect(() => {
    if (isLoyaltyEnabled && appConfig.status === 'success' && restaurant) {
      actions.getCampaignProgress(
        `fd${appConfig.data.WhiteLabelId}`,
        restaurant.PhysicalRestaurantId,
      );
    }
  }, [isLoyaltyEnabled, appConfig.status, restaurant]);

  useEffect(() => {
    const isMenuNotLoaded =
      !menu && menuUrl && menuLoadStatus.current === 'NOT_LOADED';
    const hasMenuUpdated = prevMenuUrl && prevMenuUrl !== menuUrl;

    if (isMenuNotLoaded || hasMenuUpdated) {
      getMenuByUrl(menuUrl);
      menuLoadStatus.current = 'LOADING';
    }
  }, [menu, menuUrl]);

  useEffect(() => {
    if (menu) {
      menuLoadStatus.current = 'LOADED';
    }
  }, [menu]);

  useEffect(() => {
    if (toastMessages?.length) {
      toastMessages.forEach((message) => {
        if (!pushedMessages.includes(message)) {
          enqueueSnackbar(message, {
            variant: 'info',
          });
          setPushedMessages([...pushedMessages, message]);
        }
      });
    }
  }, [toastMessages]);

  useEffect(() => {
    if (isBasketMenuLoaded && isBasketLoaded) {
      checkoutUtils.redirectToMenuScreenIfBelowMinimumOrderAmount(
        enqueueSnackbar,
        appConfig,
        basket,
        currentLang,
        params,
        restaurant,
        translate,
      );
    }
  }, [isBasketMenuLoaded, isBasketLoaded, menu, restaurant]);

  useEffect(() => {
    if (isBasketEmpty && isBasketMenuLoaded) {
      checkoutUtils.redirectToMenuIfRemovedAllItemsInBasket(
        basket.items,
        params,
        enqueueSnackbar,
        translate,
      );
    }
  }, [isBasketEmpty, isBasketMenuLoaded]);

  useEffect(() => {
    if (vouchersInProgress) {
      const newPendingVouchers = pendingVouchers.slice(1);
      setPendingVouchers(newPendingVouchers);
    }
  }, [account, customer, basket.items, restaurant]);

  useEffect(() => {
    if (orderPayload && typeof orderPayload !== 'string') {
      checkVoucherState(orderPayload);
    }

    return () => {
      cancelCheckVoucherState();
    };
  }, [orderItemTotal, basket.requestedTime]);

  useEffect(() => {
    setCurrentLang(utils.get.activeLanguageCode(languages, 'en'));
  }, [languages?.active]);

  useEffect(() => {
    if (order.state === 'failed') {
      setOrder({
        state: 'none',
        submitType: 'NORMAL',
      });
    }
  }, [order.state]);

  useEffect(() => {
    const { Request, Require } = Api.EmailRequestMode;

    if (
      customer.status === 'success' &&
      !customer.data.IsGuest &&
      [Request, Require].includes(customer.data.EmailRequestMode)
    ) {
      setShowEmailDialog(true);
    }
  }, [customer.status]);

  useEffect(() => {
    const isBasketItemNotInMenu =
      checkoutError?.code === MENU_HAS_UPDATED_ERROR_CODE && menu.MenuId;

    if (isBasketItemNotInMenu) {
      // clear basket and menu in local storage
      const menuId = menu.MenuId;
      clearBasketAndMenuLocalStorage(menuId, menuUrl);

      // refetch restaurant details for latest menu and clear basket
      menuLoadStatus.current = 'NOT_LOADED';
      getRestaurantData(vrId, type, id);
      clearBasket({ menuId });

      // toast message "The menu has been updated."
      enqueueSnackbar(checkoutError.message, {
        variant: 'error',
      });
    }
  }, [checkoutError]);

  useEffect(() => {
    if (checkoutError?.message || paymentError?.message) {
      const eventData = {
        amp_errorCode: 'n/a',
        amp_errorMessage: checkoutError?.message || paymentError?.message,
        amp_label: 'Place Order Failed',
      };
      analyticsService.track(eventData);
    }
  }, [checkoutError, paymentError]);

  if (order.state === 'paid' || customer.status !== 'success') {
    return null;
  }

  if (appConfig.status !== 'success' || !restaurant || !menu) {
    return <LoadingSpinner />;
  }

  const trackBackSelected = (): void => {
    const eventData = {
      amp_label: 'Back Selected',
      amp_view: 'Order Checkout',
    };
    analyticsService.track(eventData);
  };

  const trackRevenue = (): void => {
    basket.items.forEach((item) => {
      const eventData = {
        amp_revenueProperties: {
          price: BasketControl.ItemTotal(item),
          productId: item.Item.MenuItemId.toString(),
          quantity: 1,
        },
      };

      analyticsService.track(eventData);
    });
  };

  const trackTimeSelected = (time: string): void => {
    const getEventLabel = serviceTypes.isDelivery
      ? 'Delivery Time Selected'
      : 'Collection Time Selected';
    const isPreorder = restaurant.AcceptPreOrders && !restaurant.IsOpen;

    const eventData = {
      amp_isPreorder: isPreorder,
      amp_label: getEventLabel,
      amp_timeSelected: time,
    };
    analyticsService.track(eventData);
  };

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const tableNumberId = e.target.value.toString().replace(/\D/g, '');
    editTableNumbers(vrId, parseInt(tableNumberId) || undefined);
  };

  const handleChangeDeliveryTime = (time: string): void => {
    if (!menu || time === 'blank') {
      // TODO:DN something here, should never happen but be warned
      return;
    }
    setRequestedTime({ time, menuId: menu.MenuId });
  };

  const handleSubmit = (): void => {
    const basketSnapshot: WebOrder.State.BasketSnapshot =
      checkoutUtils.getBasketSnapshot(
        account,
        basket,
        currentLang,
        orderPayload,
        orderTotalToPay,
        percentageTip,
        pickupLocationType,
        restaurant,
        translate,
      );

    createBasketSnapshot(basketSnapshot);
    startCheckoutFlow();
    trackRevenue();
  };

  const handleTimeDialogConfirm = (): void => {
    continueCheckoutFlow();
    trackTimeSelected(basket.requestedTime);
  };

  const currency = (restaurant && restaurant.Currency) || Api.Currency.EUR;

  const extraRouteParams = concessionStoreId ? `/${concessionStoreId}` : '';
  const backRoute = `/restaurant/${vrId}/${type}/${id}${extraRouteParams}`;

  const {
    serviceTypes,
    tableServiceCategory,
    preOrderAvailable,
    allowPreOrdersAndTableService,
    isTableServiceOnly,
  } = checkoutUtils.getRestaurantLocationMeta(
    restaurant as Api.CollectionRestaurantDetail,
    type,
  );

  const disabled =
    !!checkoutValidationErrors.length ||
    (preOrderAvailable &&
      !allowPreOrdersAndTableService &&
      pickupLocationType === Api.PickupLocationType.TableService);

  const missingTime = checkoutValidationErrors.includes('requested_time');
  const missingName = checkoutValidationErrors.includes('name');
  const missingTableNumber = checkoutValidationErrors.includes('table_number');
  const isCollection = restaurant.DeliveryType === Api.DeliveryType.Collection;
  const locationAreaOptions = isCollection
    ? restaurant.LocationAreas
    : undefined;
  const pickupLocationOptions = isCollection
    ? restaurant.PickupLocationOptions
    : undefined;

  const isTableLockEnabled =
    isCollection &&
    restaurant.PickupLocationTypes.includes(1 || 2) &&
    restaurant.IsTableLockEnabled;

  const renderPopout = (): JSX.Element => {
    if (!vouchers.length || !isLoyaltyEnabled) {
      return null;
    }

    const pointerVariant = isMobileScreen ? 'right' : 'center';

    return (
      <NewVoucherPopout
        // TODO: CXFE-1592 - implement 'Apply voucher' button
        onApplyVoucher={() => {}}
        pointerVariant={pointerVariant}
        translate={translate}
        vouchers={vouchers}
      />
    );
  };

  const renderEmailDialog = (customerData: Api.Customer): JSX.Element => (
    <EmailDialog
      Close={toggleEmailDialog}
      EmailRequestMode={customerData.EmailRequestMode}
      ShowEmailDialog={showEmailDialog}
      translate={translate}
      UpdateCustomer={updateCustomer}
    />
  );

  if (isOrderProcessing) {
    const mainTextTranslationId =
      account?.PaymentAccountType === 'CashPaymentAccount'
        ? 'OrderProcessing_ProcessingOrder'
        : 'OrderProcessing_ProcessingPayment';

    return (
      <OrderProcessing
        mainText={translate(mainTextTranslationId) as TranslationId}
        subText={translate('OrderProcessing_PleaseWait') as TranslationId}
      />
    );
  }

  const isServiceChargeApplied = !basket.excludeServiceCharge;

  const orderTotalWithServiceCharge = isServiceChargeApplied
    ? orderTotalToPay + checkoutUtils.computeServiceCharge(basket, restaurant)
    : orderTotalToPay;

  const isPayGreenFFEnabled = flagService.isFlagOn('payGreen');
  const isRestaurantVoucherEnabled =
    isPayGreenFFEnabled && restaurant.AcceptRestaurantVoucherOrders;

  const storeId = restaurant.PhysicalRestaurantId;

  const isSelectTimeDialogDisabled =
    missingTime && requestedTimeOrDefault === 'blank';

  return (
    <Page pageName="order-checkout">
      <PageHeader
        params={params}
        popout={renderPopout()}
        showProfileMenu
        title={translate('Checkout') as string}
      >
        <BackLink
          label={translate('Back') as string}
          linkPath={backRoute}
          onClick={trackBackSelected}
        />
      </PageHeader>

      <article className="fd-content__main">
        {isTableLockEnabled ? (
          <TableNumberHeader
            editPickupLocationType={editPickupLocationTypes}
            locationAreas={locationAreaOptions}
            pickupLocationType={pickupLocationType}
            physicalRestaurantAddress={restaurant.PhysicalRestaurantAddress}
            serviceTypes={serviceTypes}
            tableNumber={basket.tableNumber}
            tableServiceCategory={tableServiceCategory}
            translate={translate}
            vrid={vrId}
          />
        ) : (
          <ServiceSection
            configData={appConfig.data}
            deliveryLocation={deliveryLocation}
            disabled={disabled}
            handleChangeDeliveryTime={handleChangeDeliveryTime}
            handleNumber={handleNumberChange}
            hasRequestFullNameFeatureFlag={hasRequestFullNameFeatureFlag}
            loading={loading}
            params={params}
            pickupLocationType={pickupLocationType}
            requestedTimeOrDefault={requestedTimeOrDefault}
            restaurant={restaurant}
            serviceTypes={serviceTypes}
            setUserName={setUserName}
            tableNumber={tableNumber}
            toggleTableDialog={toggleTableDialog}
            translate={translate}
            type={type}
            userName={userName}
          />
        )}
        <BasketSection
          account={account}
          addItemToBasket={addItemToBasket}
          appConfig={appConfig}
          basket={basket}
          chefNotes={chefNotes}
          currentLang={currentLang}
          customer={customer}
          editChefNotes={editChefNotes}
          isRoundUpEnabled={isRoundUpEnabled}
          loyaltyCampaign={loyaltyCampaign}
          menu={menu}
          orderItemTotal={orderItemTotal}
          orderPayload={orderPayload}
          orderRoundUpAmount={orderRoundUpAmount}
          orderTipsTotal={orderTipsTotal}
          orderTotal={orderTotal}
          orderTotalToPay={orderTotalWithServiceCharge}
          params={params}
          percentageTip={percentageTip}
          pickupLocationType={pickupLocationType}
          removeItemFromBasket={removeItemFromBasket}
          restaurant={restaurant}
          setRoundUpEnabled={setRoundUpEnabled}
          setTip={setTip}
          tableNumber={tableNumber}
          toggleExcludeServiceCharge={toggleExcludeServiceCharge}
          toggleVoucherDialog={toggleVoucherDialog}
          translate={translate}
        />

        {isRestaurantVoucherEnabled && (
          <RestaurantVoucherSection
            currency={currency}
            orderTotalToPay={orderTotalWithServiceCharge}
            storeId={storeId}
          />
        )}

        <PaymentSection
          currency={currency}
          currentLanguage={currentLang}
          disabled={disabled}
          errors={{
            checkoutError,
            paymentError,
          }}
          loading={loading}
          onSubmit={handleSubmit}
          payments={payments}
          price={orderTotalWithServiceCharge}
          selectedPayment={account}
          translate={translate}
        />
      </article>
      <Hidden xsDown>
        <section className="fd-content__side">
          <Suspense fallback={<div />}>
            <ProfilePanel
              params={{
                id: id?.toString(),
                type,
                vrid: vrId.toString(),
              }}
            />
            {renderPopout()}
          </Suspense>
        </section>
      </Hidden>
      <AddCreditCardDialog
        addCardPayment={creditCardPaymentAdded}
        cancelAddCardBridge={cancelAddCardBridge}
        clearBasketStatus={clearBasketStatus}
        configData={appConfig.data}
        currency={currency}
        currentLanguage={currentLang}
        isFromCheckoutPage
        orderTotalToPay={orderTotalWithServiceCharge}
        savedCardCount={2}
        setShowDialog={setShowAddCardDialog}
        showDialog={showAddCardDialog}
        translate={translate}
      />

      <VoucherDialog
        checkUserVoucher={checkUserVoucher}
        handleClose={toggleVoucherDialog}
        menu={menu}
        orderPayload={orderPayload}
        showVoucherDialog={showVoucherDialog}
        translate={translate}
        voucher={basket.voucher}
      />

      {customer.status === 'success' && renderEmailDialog(customer.data)}

      <CampaignConsentDialog customer={customer} />

      <TableSelectDialog
        handleClose={toggleTableDialog}
        handleNumber={handleNumberChange}
        handleSubmit={handleSubmit}
        pickupLocationOptions={pickupLocationOptions}
        locationAreas={locationAreaOptions}
        placeOrderAfterTableSelect={false}
        showTableServiceDialog={showTableServiceDialog}
        tableNumber={tableNumber?.toString()}
        tableServiceCategory={tableServiceCategory}
        translate={translate}
      />

      <SelectTimeDialog
        checkoutValidationErrors={checkoutValidationErrors}
        disabled={isSelectTimeDialogDisabled}
        nameInputProps={
          missingName && {
            hasRequestFullNameFeatureFlag,
            isDelivery: type === 'delivery',
            isTableService: isTableServiceOnly,
            onChange: (name: string): void => {
              setUserName({ name });
            },
            value: userName,
          }
        }
        onCancel={cancelCheckoutFlow}
        onOk={handleTimeDialogConfirm}
        preOrderTimeProps={
          missingTime && {
            isDelivery: type === 'delivery',
            isTableService: isTableServiceOnly,
            onChange: (time: string): void => {
              setRequestedTime({
                time,
                menuId: menu.MenuId,
              });
            },
            options:
              restaurant &&
              restaurant.PreOrderOptions &&
              restaurant.PreOrderOptions.PreOrderTimes,
            value: requestedTimeOrDefault,
          }
        }
        selectedPayment={account}
        show={missingTime || missingName || missingTableNumber}
        tableNumberInputProps={
          missingTableNumber && {
            category: tableServiceCategory,
            locationAreaOptions,
            onChange: (tableNumberSelection?: number): void => {
              editTableNumbers(vrId, tableNumberSelection);
            },
            options: pickupLocationOptions,
            value: tableNumber,
          }
        }
        translate={translate}
      />
    </Page>
  );
}
